import { create } from 'zustand';
import apiClient from '../lib/api';

/** Fields every celebration carries so reactions/comments can be keyed to it. */
export interface EventIdentity {
  eventType: 'birthday' | 'anniversary' | 'new_joiner' | 'festival' | 'award';
  eventKey: string;
  eventDate: string;
  reactions: Record<string, number>;
  myReactions: string[];
  commentCount: number;
  templateBody?: string | null;
}

export interface Celebrant extends EventIdentity {
  employeeId: string;
  name: string;
  firstName: string;
  photoUrl: string | null;
  department?: string | null;
  designation?: string | null;
  years?: number;
  joinDate?: string;
}

export interface Festival extends EventIdentity {
  id: string;
  name: string;
  date: string;
  type: string;
  description?: string;
  bannerUrl?: string | null;
  wishText?: string | null;
}

export interface FestivalRow {
  id: string;
  name: string;
  date: string;
  type: string;
  bannerUrl?: string | null;
  wishText?: string | null;
}

export interface AwardFeedItem extends EventIdentity {
  id: string;
  title: string;
  description?: string;
  awardDate: string;
  employeeName: string | null;
  department?: string | null;
  designation?: string | null;
  photoUrl: string | null;
}

export interface EngagementComment {
  id: string;
  userId: string;
  author: string;
  body: string;
  createdAt: string;
}

export interface TodayCelebrations {
  birthdays: Celebrant[];
  anniversaries: Celebrant[];
  newJoiners: Celebrant[];
  festivals: Festival[];
  awards: AwardFeedItem[];
}

export interface RosterRow {
  id: string;
  employeeCode: string;
  name: string;
  email: string;
  department?: string;
  designation?: string;
  joinDate?: string;
  dateOfBirth?: string | null;
  photoUrl?: string | null;
}

export interface Award {
  id: string;
  employeeId: string | null;
  employeeName: string | null;
  title: string;
  description?: string;
  awardDate: string;
  photoUrl: string | null;
}

interface EngagementState {
  today: TodayCelebrations | null;
  roster: RosterRow[];
  awards: Award[];
  festivals: FestivalRow[];
  templates: Record<string, string>;
  isLoading: boolean;
  error: string | null;

  fetchToday: () => Promise<void>;
  fetchRoster: (search?: string) => Promise<void>;
  updateEmployee: (employeeId: string, data: { dateOfBirth?: string | null; photoUrl?: string | null; joinDate?: string | null }) => Promise<void>;
  fetchAwards: () => Promise<void>;
  createAward: (data: { employeeId?: string | null; title: string; description?: string; awardDate: string; photoUrl?: string | null }) => Promise<void>;
  updateAward: (id: string, data: { employeeId?: string | null; title?: string; description?: string; awardDate?: string; photoUrl?: string | null }) => Promise<void>;
  deleteAward: (id: string) => Promise<void>;

  fetchTemplates: () => Promise<void>;
  updateTemplate: (eventType: string, body: string | null) => Promise<void>;

  fetchFestivals: () => Promise<void>;
  createFestival: (data: { name: string; date: string; bannerUrl?: string | null; wishText?: string | null }) => Promise<void>;
  updateFestival: (id: string, data: { bannerUrl?: string | null; wishText?: string | null }) => Promise<void>;
  deleteFestival: (id: string) => Promise<void>;

  toggleReaction: (ev: { eventType: string; eventKey: string; eventDate: string; emoji: string }) => Promise<{ reactions: Record<string, number>; myReactions: string[] }>;
  listComments: (ev: { eventType: string; eventKey: string; eventDate: string }) => Promise<EngagementComment[]>;
  addComment: (ev: { eventType: string; eventKey: string; eventDate: string; body: string }) => Promise<EngagementComment>;
}

const unwrap = (res: any) => res.data?.data ?? res.data;

export const useEngagementStore = create<EngagementState>((set, get) => ({
  today: null,
  roster: [],
  awards: [],
  festivals: [],
  templates: {},
  isLoading: false,
  error: null,

  fetchToday: async () => {
    try {
      const res = await apiClient.get('/people/engagement/today');
      set({ today: unwrap(res) });
    } catch (error: any) {
      // The banner is best-effort; a failure should never surface an error toast.
      set({ today: { birthdays: [], anniversaries: [], newJoiners: [], festivals: [], awards: [] } });
    }
  },

  fetchRoster: async (search?: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/engagement/roster', { params: search ? { search } : {} });
      set({ roster: unwrap(res) || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to load employees', isLoading: false });
    }
  },

  updateEmployee: async (employeeId, data) => {
    await apiClient.patch(`/people/engagement/roster/${employeeId}`, data);
    set((state) => ({
      roster: state.roster.map((r) =>
        r.id === employeeId
          ? {
              ...r,
              dateOfBirth: data.dateOfBirth !== undefined ? data.dateOfBirth : r.dateOfBirth,
              photoUrl: data.photoUrl !== undefined ? data.photoUrl : r.photoUrl,
              joinDate: data.joinDate !== undefined ? (data.joinDate ?? undefined) : r.joinDate,
            }
          : r,
      ),
    }));
  },

  fetchAwards: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/engagement/awards');
      set({ awards: unwrap(res) || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to load awards', isLoading: false });
    }
  },

  createAward: async (data) => {
    await apiClient.post('/people/engagement/awards', data);
    await get().fetchAwards();
  },

  updateAward: async (id, data) => {
    await apiClient.patch(`/people/engagement/awards/${id}`, data);
    await get().fetchAwards();
  },

  deleteAward: async (id) => {
    await apiClient.delete(`/people/engagement/awards/${id}`);
    set((state) => ({ awards: state.awards.filter((a) => a.id !== id) }));
  },

  fetchTemplates: async () => {
    try {
      const res = await apiClient.get('/people/engagement/templates');
      set({ templates: unwrap(res) || {} });
    } catch { /* best-effort */ }
  },

  updateTemplate: async (eventType, body) => {
    await apiClient.patch(`/people/engagement/templates/${eventType}`, { body });
    set((state) => {
      const next = { ...state.templates };
      if (body && body.trim()) next[eventType] = body; else delete next[eventType];
      return { templates: next };
    });
  },

  fetchFestivals: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/people/engagement/festivals');
      set({ festivals: unwrap(res) || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to load festivals', isLoading: false });
    }
  },

  createFestival: async (data) => {
    await apiClient.post('/people/engagement/festivals', data);
    await get().fetchFestivals();
  },

  updateFestival: async (id, data) => {
    await apiClient.patch(`/people/engagement/festivals/${id}`, data);
    set((state) => ({
      festivals: state.festivals.map((f) =>
        f.id === id
          ? {
              ...f,
              bannerUrl: data.bannerUrl !== undefined ? data.bannerUrl : f.bannerUrl,
              wishText: data.wishText !== undefined ? data.wishText : f.wishText,
            }
          : f,
      ),
    }));
  },

  deleteFestival: async (id) => {
    await apiClient.delete(`/people/engagement/festivals/${id}`);
    set((state) => ({ festivals: state.festivals.filter((f) => f.id !== id) }));
  },

  toggleReaction: async (ev) => {
    const res = await apiClient.post('/people/engagement/react', ev);
    return unwrap(res);
  },

  listComments: async (ev) => {
    const res = await apiClient.get('/people/engagement/comments', { params: ev });
    return unwrap(res) || [];
  },

  addComment: async (ev) => {
    const res = await apiClient.post('/people/engagement/comments', ev);
    return unwrap(res);
  },
}));
